/**
 * Predicted-next-step chip (PRODUCT_SPEC §4.96 P6).
 *
 * Drops into Run Monitor or Case Client to show "Likely next step + ETA".
 * Self-contained — fetches /api/process-intel/{source}/{def_id}/predict_next
 * for the live run_id/case_id and renders a compact chip. License-gated:
 * if the workspace doesn't have process_intel.prediction (Enterprise) the
 * chip renders nothing instead of a 402 error.
 */
import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from './config';

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function fmtRemaining(ms) {
  const n = Number(ms || 0);
  if (!n) return '';
  if (n < 1000) return `${Math.round(n)} ms`;
  if (n < 60_000) return `${Math.round(n / 1000)} s`;
  if (n < 3_600_000) return `${Math.round(n / 60_000)} min`;
  return `${(n / 3_600_000).toFixed(1)} h`;
}

const PALETTE = {
  bg: 'var(--bg-secondary, #1f2937)',
  border: 'var(--border-color, #374151)',
  text: 'var(--text-primary, #e5e7eb)',
  faint: 'var(--text-tertiary, #9ca3af)',
  accent: '#a78bfa',
};

export default function PredictedNextChip({
  sourceType,            // 'workflow_run' or 'case'
  sourceDefId,           // workflow_id or case_type_id
  runId,                 // optional — for workflow_run
  caseId,                // optional — for case
  topK = 3,
  refreshKey,            // bump to force-refetch
  compact = false,
}) {
  const [data, setData] = useState(null);
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!sourceType || !sourceDefId || (!runId && !caseId)) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const qs = new URLSearchParams();
        if (runId) qs.set('run_id', runId);
        if (caseId) qs.set('case_id', caseId);
        qs.set('top_k', String(topK));
        const r = await fetch(
          `${API_BASE_URL}/api/process-intel/${sourceType}/${sourceDefId}/predict_next?${qs}`,
          { headers: authHeaders() },
        );
        if (cancelled) return;
        if (r.status === 402) { setLocked(true); setData(null); return; }
        if (!r.ok) { setData(null); return; }
        setLocked(false);
        setData(await r.json());
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sourceType, sourceDefId, runId, caseId, topK, refreshKey]);

  if (locked) return null;
  if (!data || !data.predictions || data.predictions.length === 0) {
    return loading ? <span style={{ color: PALETTE.faint, fontSize: 11 }}>Predicting…</span> : null;
  }

  const top = data.predictions[0];
  const rest = data.predictions.slice(1);
  const endish = top.step === '[END]';
  const remaining = endish ? data.history_remaining_time_ms : top.mean_remaining_time_ms;
  const probPct = Math.round((top.probability || 0) * 100);

  if (compact) {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: PALETTE.bg, border: `1px solid ${PALETTE.border}`,
        borderRadius: 12, padding: '2px 10px', fontSize: 11,
        color: PALETTE.text,
      }} title={`Markov ${data.order_used} · trained on ${data.trace_count} traces`}>
        <span style={{ color: PALETTE.accent }}>▶</span>
        {endish ? 'Likely to complete' : (
          <>Next: <b>{top.step}</b></>
        )}
        <span style={{ color: PALETTE.faint }}>· {probPct}%</span>
        {remaining ? (
          <span style={{ color: PALETTE.faint }}>· ~{fmtRemaining(remaining)}</span>
        ) : null}
      </span>
    );
  }

  return (
    <div style={{
      background: PALETTE.bg, border: `1px solid ${PALETTE.border}`,
      borderRadius: 8, padding: '8px 12px', fontSize: 12, color: PALETTE.text,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 4,
      }}>
        <div style={{ fontWeight: 700, color: PALETTE.accent }}>🔮 Predicted next</div>
        <div style={{ color: PALETTE.faint, fontSize: 10 }}>
          {data.order_used} · {data.trace_count} traces
        </div>
      </div>
      <div>
        <span style={{ fontWeight: 700 }}>{endish ? 'Likely to complete' : top.step}</span>
        <span style={{ color: PALETTE.faint, marginLeft: 8 }}>
          {probPct}% {remaining ? `· ~${fmtRemaining(remaining)} remaining` : ''}
        </span>
      </div>
      {rest.length > 0 && (
        <div style={{ marginTop: 4, color: PALETTE.faint, fontSize: 11 }}>
          {rest.map((p, i) => (
            <span key={p.step + i} style={{ marginRight: 8 }}>
              {p.step === '[END]' ? '(end)' : p.step}{' '}
              <span style={{ opacity: 0.7 }}>{Math.round((p.probability || 0) * 100)}%</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
